import React from "react";
import MultiSelectDropdown from "../../common/MultiSelectDropdown";
import MultiSelectWithFreeText from "../../common/MultiSelectWithFreeText";
import StaffProfileDropdownWithFreeText from "../../common/StaffProfileDropdownWithFreeText";

const TechnicalSkillsSection = ({
  isEditing,
  formData,
  handleInputChange,
  videoAnalysisOptions,
  gpsPerformanceOptions,
  softwareOptions,
  strengthConditioningOptions,
  managementResponsibilitiesOptions,
  areasOfResponsibilityOptions,
  toolsUsedOptions,
  areaOfSpecializationOptions,
  specializationOptions,
}) => {
  const showStrengthConditioning = formData.position === "strength_conditioning_coach";
  const showTechnicalDirector = formData.position === "technical_director";
  const showSportingDirector = formData.position === "sporting_director";
  const showScout = formData.position === "scout";
  const showPhysio = formData.position === "physiotherapist";
  const showNutritionist = formData.position === "nutritionist";
  const showPerformanceAnalyst = formData.position === "performance_analyst";

  const showDefaultSpecializations = ![
    "strength_conditioning_coach",
    "technical_director",
    "sporting_director",
    "scout",
    "physiotherapist",
    "nutritionist",
    "performance_analyst",
  ].includes(formData.position);

  return (
    <div className="bg-white p-6 border border-[#E6E6E6]">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-[11px] font-bold text-[#3C3C3C]">TECHNICAL SKILLS & SPECIALIZATIONS</h2>
      </div>

      <div className="mb-6">
        <MultiSelectDropdown
          label="Video/Analysis"
          value={formData.videoAnalysis}
          onChange={(value) => handleInputChange("videoAnalysis", value)}
          options={videoAnalysisOptions}
          placeholder="Select"
          disabled={!isEditing}
        />
      </div>

      <div className="mb-6">
        <MultiSelectDropdown
          label="GPS/Performance"
          value={formData.gpsPerformance}
          onChange={(value) => handleInputChange("gpsPerformance", value)}
          options={gpsPerformanceOptions}
          placeholder="Select"
          disabled={!isEditing}
        />
      </div>

      <div className="mb-6">
        <MultiSelectDropdown
          label="Software"
          value={formData.software}
          onChange={(value) => handleInputChange("software", value)}
          options={softwareOptions}
          placeholder="Select"
          disabled={!isEditing}
        />
      </div>

      {showStrengthConditioning && (
        <div className="mb-6">
          <MultiSelectDropdown
            label="Specializations"
            value={formData.specializations}
            onChange={(value) => handleInputChange("specializations", value)}
            options={strengthConditioningOptions}
            placeholder="Select"
            disabled={!isEditing}
          />
        </div>
      )}

      {showTechnicalDirector && (
        <div className="mb-6">
          <MultiSelectDropdown
            label="Management Responsibilities"
            value={formData.managementResponsibilities}
            onChange={(value) => handleInputChange("managementResponsibilities", value)}
            options={managementResponsibilitiesOptions}
            placeholder="Select"
            disabled={!isEditing}
          />
        </div>
      )}

      {showSportingDirector && (
        <div className="mb-6">
          <MultiSelectDropdown
            label="Areas of Responsibility"
            value={formData.areasOfResponsibility}
            onChange={(value) => handleInputChange("areasOfResponsibility", value)}
            options={areasOfResponsibilityOptions}
            placeholder="Select"
            disabled={!isEditing}
          />
        </div>
      )}

      {showScout && (
        <div className="mb-6">
          <MultiSelectWithFreeText
            label="Tools Used"
            value={formData.toolsUsed}
            onChange={(value) => handleInputChange("toolsUsed", value)}
            options={toolsUsedOptions}
            placeholder="Select tools or type..."
            disabled={!isEditing}
          />
        </div>
      )}

      {showPhysio && (
        <div className="mb-6">
          <StaffProfileDropdownWithFreeText
            label="Area of Specialization"
            value={formData.areaOfSpecialization}
            onChange={(value) => handleInputChange("areaOfSpecialization", value)}
            options={areaOfSpecializationOptions}
            placeholder="Select specialization or type..."
            disabled={!isEditing}
          />
        </div>
      )}

      {showNutritionist && (
        <div className="mb-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Sample Nutrition Plans</label>
            {formData.sampleNutritionPlans ? (
              <div className="flex items-center gap-3 p-3 bg-[#F8F9FB] border border-gray-300 rounded-md">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 2H4C3.44772 2 3 2.44772 3 3V17C3 17.5523 3.44772 18 4 18H16C16.5523 18 17 17.5523 17 17V7L11 2H8Z" stroke="#006BF3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M11 2V7H17" stroke="#006BF3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="text-sm text-gray-700">{formData.sampleNutritionPlans.name}</span>
                {isEditing && (
                  <button
                    onClick={() => handleInputChange("sampleNutritionPlans", null)}
                    className="ml-auto text-red-500 hover:text-red-700"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center w-full h-20 border-2 border-dashed border-gray-300 rounded-md bg-[#F8F9FB]">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    if (file) handleInputChange("sampleNutritionPlans", file);
                  }}
                  className="hidden"
                  id="nutrition-plans-upload"
                  disabled={!isEditing}
                />
                <label htmlFor="nutrition-plans-upload" className={`cursor-pointer text-sm text-gray-500 ${!isEditing ? 'cursor-not-allowed opacity-50' : ''}`}>
                  {isEditing ? "Click to upload nutrition plans" : "No file uploaded"}
                </label>
              </div>
            )}
          </div>
        </div>
      )}

      {showPerformanceAnalyst && (
        <div className="mb-6">
          <MultiSelectWithFreeText
            label="Tools Used"
            value={formData.toolsUsed}
            onChange={(value) => handleInputChange("toolsUsed", value)}
            options={toolsUsedOptions}
            placeholder="Select tools or type..."
            disabled={!isEditing}
          />
        </div>
      )}

      {showDefaultSpecializations && (
        <div className="mb-6">
          <MultiSelectWithFreeText
            label="Specializations"
            value={formData.specializations}
            onChange={(value) => handleInputChange("specializations", value)}
            options={specializationOptions || []}
            placeholder="Select"
            disabled={!isEditing}
          />
        </div>
      )}
    </div>
  );
};

export default TechnicalSkillsSection;

